// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { ElementRef, Injectable, NgZone } from '@angular/core';
import { Assignees, CustomAnnotation, FieldTypes, GuidedSigningState, PdfField, ThirdPartyType } from '@interfaces/document.interfaces';
import WebViewer, { Core, UI, WebViewerInstance } from '@pdftron/webviewer';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, firstValueFrom, forkJoin, lastValueFrom, of } from 'rxjs';
import { environment } from '../../environments/environment';
import {
    fieldHeightByType,
    fieldWidthByType,
    getAssigneeFromName,
    getColorsByAssignee,
    getDdjField,
    getTypeFromName,
    resetPriority,
} from '../utils/pdf-form-utils';
import { bufferArrayToBase64, getHtmlCenter } from '../utils/utils';
import { UserService } from './user.service';
// eslint-disable-next-line no-undef
import SignatureCreateTool = Core.Tools.SignatureCreateTool;
import { ViewerInteractionOptions } from '@interfaces/pdftron.interfaces';
import { ErrorService } from './error.service';

@Injectable({
    providedIn: 'root',
})
export class PdftronInteractionsService {
    // displayXandY = new BehaviorSubject({x : '0', y :'0'});
    baselineContent;
    baselineContenturl;
    dropPoint: { x: number; y: number };
    fieldIteration = 0;
    fields: { [key: string]: PdfField } = {};
    signingSessionData: { [key: string]: PdfField } = {};
    fieldsBkup: { [key: string]: PdfField } = {};
    parentContainerRef;
    pdfTron: WebViewerInstance | typeof UI | typeof Core | any;
    pdfViewerRef: ElementRef;
    signature: any;
    signaturePointData: any;
    initials: any;
    initialsPointData: any;
    isMobileDevice = false;
    isDocumentComplete = false;
    webviewerInError = false;
    public iFrameLoaded: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public isGuidedSigningOn: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public signaturesReady: BehaviorSubject<boolean> = new BehaviorSubject(false);
    customFieldData: CustomAnnotation[] | null; //Cannot find the type for the custom field in annotationChanged events :\

    isCtrlDown = false;
    public coordinateEvent: any;
    public loadingDocument = false;

    constructor(
        private readonly zone: NgZone,
        private readonly deviceService: DeviceDetectorService,
        private readonly userService: UserService,
        private readonly errorService: ErrorService
    ) {}

    isiFrameLoaded() {
        return this.iFrameLoaded;
    }

    createWebViewerForEditing(pdfViewerRef?: ElementRef, options?: ViewerInteractionOptions): Promise<WebViewerInstance> {
        this.isMobileDevice = !this.deviceService.isDesktop();
        this.pdfViewerRef = pdfViewerRef || this.pdfViewerRef;
        if (this.pdfTron?.UI) {
            return lastValueFrom(of(this.pdfTron));
        }
        return WebViewer(
            {
                fullAPI: true,
                licenseKey: environment.pdfTronKey,
                path: '../../../lib',
                css: '/assets/pdftron-small-screen.css',
                disabledElements: [
                    'header',
                    'toolsHeader',
                    'toolsOverlay',
                    'ribbons',
                    'toggleNotesButton',
                    'searchButton',
                    'menuButton',
                    'rubberStampToolGroupButton',
                    'stampToolGroupButton',
                    'fileAttachmentToolGroupButton',
                    'calloutToolGroupButton',
                    'undo',
                    'redo',
                    'eraserToolButton',
                    'signatureModal',
                    'annotationCommentButton',
                    'annotationStyleEditButton',
                    'linkButton',
                    'colorPalette',
                    'richTextPopup',
                    'contextMenuPopup',
                ],
            },
            this.pdfViewerRef.nativeElement
        ).then((instance: WebViewerInstance) => {
            const iframeDoc = instance.UI.iframeWindow.document;
            const container = iframeDoc.querySelector('.document-content-container') as HTMLDivElement;

            this.signaturesReady.next(false);

            // provide more space for desktop signing if needed
            if (container && !this.isMobileDevice) {
                container.style.paddingTop = '0.5rem';
                container.style.paddingBottom = '0.5rem';
            }

            // Verify the right printing options are turned on
            instance.UI.setPrintQuality(5.2);
            instance.UI.useEmbeddedPrint(true);
            this.pdfTron = instance;
            const { documentViewer, Annotations } = instance.Core;

            const manager = instance.Core.documentViewer.getDisplayModeManager();
            const newDisplay = new instance.Core.DisplayMode(documentViewer, instance.Core.DisplayModes.Continuous, true);
            manager.setDisplayMode(newDisplay);

            instance.UI.addEventListener('dragover', this.dragOver);
            instance.UI.addEventListener('drop', (e) => {
                this.drop(e, instance);
            });

            (instance.Core.annotationManager as Core.AnnotationManager).addEventListener(
                'annotationChanged',
                (event, action, info: Core.AnnotationManager.AnnotationChangedInfoObject) => {
                    // triggerDeleteEvent is not mandatory which is why we need to look for not false
                    if (action === 'delete' && options?.triggerDeleteEvent !== false) {
                        //This data needs to be saved for the next annotationChanged event
                        if (info?.source === 'draggingAcrossPages') {
                            this.customFieldData = event?.map((e) => e.custom);
                        }
                        this.deleteFields(event);
                    } else {
                        if (!this.isCtrlDown) {
                            if (action === 'add' && info?.source !== 'draggingAcrossPages') {
                                this.fieldIteration++;
                            }
                            //Utilize the data saved from the previous annotationChanged event, then clear it
                            if (this.customFieldData) {
                                this.customFieldData.forEach((c, i) => (event[i].custom = c));
                                this.customFieldData = null;
                            }
                            this.moveEvent(event);
                        } else {
                            this.handleCopyPasteEvent(event);
                        }
                    }
                }
            );

            // noSignatures is used for viewing a document or when it is upload only
            if (!options?.noSignatures) {
                this.placeSignatureEventHandler(instance, options?.allowNoSignature);
            }

            // Load signature point data for future processing
            if (options?.loadSignaturePointData) {
                this.setSignaturePointData();
            }

            // When a signature is added we make sure that the user cannot manipulate it
            instance.Core.documentViewer.getTool(instance.Core.Tools.ToolNames.SIGNATURE).addEventListener('signatureReady', (signature) => {
                signature.ReadOnly = true;
            });

            documentViewer.addEventListener('documentLoaded', () => {
                instance.UI.setToolMode('Pan');
                if (this.isMobileDevice) {
                    instance.UI.setFitMode('FitWidth');
                    instance.UI.enableNativeScrolling();
                }
                instance.Core.Tools.Tool.disallowAnnotationsOutsidePage();
                instance.Core.Tools.Tool.disableTextSelection();
            });

            this.webviewerInError = false;
            instance.UI.addEventListener('loaderror', (error) => {
                this.zone.run(() => {
                    if (this.webviewerInError === false) {
                        console.error(error);
                        instance.UI.displayErrorMessage('Could not load document.');
                        const errorResponse = this.errorService.showErrorAtService({
                            message:
                                'There was an error loading this document. Please click on the Close button so it can be reloaded. If the issue persists, contact the dealer.',
                            title: 'We Encountered an Issue',
                        });

                        errorResponse.afterClosed().subscribe(() => {
                            location.reload();
                        });

                        this.webviewerInError = true;
                    }
                });
            });

            this.pdfTron = instance;
            return instance;
        });
    }

    async setSignaturePointData() {
        try {
            const pointData = await firstValueFrom(this.userService.getCurrentUserSignaturesPointData());
            if (pointData.data) {
                this.signaturePointData = JSON.parse(pointData.data?.signature)?.signature;
                this.initialsPointData = JSON.parse(pointData.data?.initials)?.signature;
            }
        } catch (ex) {
            console.error(ex);
        }
    }

    /**
     * @deprecated Was not updated last time pdftron was updated. pdftron has deprecated functions in this. Got the ok to remove feature per Ron. ccox 4-23-25
     */
    createCopyPasteEventListeners(instance: WebViewerInstance) {
        instance.Core.documentViewer.addEventListener('keyDown', (event) => {
            const selected = instance.Core.annotationManager.getSelectedAnnotations();

            // Meta for MacOS CMD+C
            if (event.key === 'Control' || event.key === 'Meta') {
                this.isCtrlDown = true;
            } else if (event.key === 'c' && this.isCtrlDown && selected.length > 0) {
                // turn on mouse over event listener
                instance.Core.documentViewer.addEventListener('mouseMove', (e) => {
                    this.coordinateEvent = e;
                });
            } else if (event.key === 'v' && this.isCtrlDown) {
                // turn off mouse over listener as object has been pasted
                instance.Core.documentViewer.removeEventListener('mouseMove'); // needs function
                //instance.Core.documentViewer.off('mouseMove'); deprecated

                // action completed. Turn off copy/paste handling
                this.isCtrlDown = false;
            }
        });

        instance.Core.documentViewer.addEventListener('keyUp', (event) => {
            // Meta for MacOS CMD+C
            if (event.key === 'Control' || event.key === 'Meta') {
                this.isCtrlDown = false;
            }

            //This fires even in text fields causing errors in the console.
            if (event.key === 'v') {
                // turn off mouse over listener as object has been pasted
                //instance.Core.documentViewer.off('mouseMove');
                //instance.Core.documentViewer.removeEventListener('mouseMove', fn);
                //https://docs.apryse.com/web/guides/events/viewer-events
                // // documentViewer.addEventListener('mouseMove', evt => {
                // //     const annot = annotationManager.getAnnotationByMouseEvent(evt);
                // //     if (annot) {
                // //       console.log("onHover: " + annot.Id);
                // //     }
                // //   });
                // // });
            }
        });
    }

    addFields(fields: { [key: string]: PdfField }, assignee?: Assignees, dealAnnotationLoaded = false, signedMetaData?: { [key: string]: PdfField }[]) {
        const { documentViewer } = this.pdfTron.Core;
        const doc = documentViewer.getDocument();
        const pageCount = doc.getPageCount();

        Object.keys(fields).forEach((key) => {
            const current = fields[key];
            let addAnnotation = true;
            let signedField = false;

            // For PDDJ Laser F&I Forms where the uploaded PDF is shorter than the
            // F&I form's PDF we do not want to include those fields
            if (current?.page > pageCount) {
                return;
            }

            addAnnotation = this.handleRouteOneFieldProcessing(current, doc);

            // Only add the field if we want to view that assignee's fields
            if (assignee && !(assignee === current.assignee || (current.assignee === Assignees.deal && dealAnnotationLoaded))) {
                addAnnotation = false;
            }

            try {
                if (signedMetaData !== null && signedMetaData !== undefined && addAnnotation) {
                    signedField = signedMetaData.find((x) => Object.keys(x).includes(key)) !== undefined;
                }
            } catch (error) {
                console.error('error:', current);
            }

            if (addAnnotation && !signedField && !current.value && current.name) {
                this.addField(this.getTextAnnotations(current));
            }
        });
    }

    addSignature(base64Image: string) {
        const { Tools, documentViewer } = this.pdfTron.Core;
        const signatureTool = <SignatureCreateTool>documentViewer.getTool('AnnotationCreateSignature');
        signatureTool.importSignatures([base64Image]);
    }

    private async placeSignatureEventHandler(instance, allowNoSignature = false) {
        const { documentViewer } = instance.Core;
        const annotationManager = documentViewer.getAnnotationManager();
        const signatureTool = documentViewer.getTool('AnnotationCreateSignature');

        await this.getSignatureImages(allowNoSignature);

        signatureTool.addEventListener('locationSelected', async (event, widget) => {
            // Keep a list of the annots underneath the event
            const affectedAnnots = [];

            annotationManager.getAnnotationsList().forEach(async (annot) => {
                // Check that we're on the right page
                if (annot.Subject === 'Widget' && annot.PageNumber === event.pageNumber) {
                    const contains = annot.X <= event.x && event.x <= annot.X + annot.Width && annot.Y <= event.y && event.y <= annot.Y + annot.Height;
                    // Check that the click was inside the boundaries for the annotation
                    if (contains) {
                        // Check the field name to see which signature to use
                        const fieldName = annot.fieldName;
                        if (fieldName) {
                            // add affect annotation to the list to be processed
                            affectedAnnots.push(annot);
                        }
                    }
                }
            });

            let chosenWidget;

            // Check to see if multiple widgets were in x/y coordinate
            if (affectedAnnots.length === 1) {
                // no overlap all is swell in the world
                chosenWidget = affectedAnnots[0];
            } else {
                // Something is up. We need to check exactly which field should be updated.
                // We will compare the x/y coordinate of the event and the annot top left.
                let totalDifference;
                // Go through and check which widget left edge is closest to the coordinate.
                affectedAnnots.forEach((annot) => {
                    const xDifference = event.x - annot.X;
                    const yDifference = event.y - annot.Y;

                    // Adding because the y coordinates go up from top to bottom
                    if (totalDifference === undefined || xDifference + yDifference < totalDifference) {
                        totalDifference = xDifference + yDifference;
                        chosenWidget = annot;
                    }
                });
            }

            if (chosenWidget) {
                const signature = await this.getSignatureImage(chosenWidget);
                try {
                    await signatureTool.setSignature(signature);
                } catch (ex) {
                    console.error('Error thrown setting the signature');
                }

                try {
                    await signatureTool.addSignature();
                } catch (ex) {
                    console.error('Error thrown adding the signature');
                }
            } else {
                // Could not find a widget to add a signature to.
                // Throw up the error modal letting them know that we can't add the signature.
            }
        });
    }

    async getSignatureImage(annot) {
        if (annot.fieldName) {
            if (annot.fieldName.includes('LEGAL_NAME')) {
                annot.innerElement.width = '400px';
                const element = document.createElement('canvas');
                element.height = annot.getHeight();
                element.width = annot.getWidth();
                const ctx = element.getContext('2d');
                ctx.font = '100 13px Courier';
                ctx.fillStyle = 'black';
                // -5 is to prevent letters like g and y from having the bottom cut off
                ctx.fillText(this.userService.user.legalName, 0, element.height - 5);
                const dataUrl = element.toDataURL();
                element.remove();
                return dataUrl;
            } else {
                // just a precaution
                if (this.initials === undefined || this.signature === undefined) {
                    await this.getSignatureImages();
                }
                const result = annot.fieldName.includes('INITIALS') ? this.initials : this.signature;
                const base64 = `${'data:image/svg;base64,'}${bufferArrayToBase64(result.data)}`;
                return base64;
            }
        } else {
            return null;
        }
    }

    handleCopyPasteEvent(event: any) {
        // TODO: Figure out a way to handle copy/paste with multiple annotations...
        event.forEach((activeField) => {
            // Handle CTRL-V scenario
            const coordinates = this.coordinateEvent?.data?.pageCoordinate;

            if (coordinates) {
                // Assumed we want to field to be pasted in the middle
                activeField.X = coordinates.x;

                if (coordinates.x > activeField.Width / 2) {
                    activeField.X = coordinates.x - activeField.Width / 2;
                }

                activeField.Y = coordinates.y;
                activeField.PageNumber = coordinates.pageNumber;

                // name of field that was copied
                let name = activeField.getContents();
                const assignee = getAssigneeFromName(name);
                const type: FieldTypes = getTypeFromName(assignee, name) as FieldTypes;

                name = this.getFieldName(assignee, type);
                activeField.setContents(name);

                const pdfField = <PdfField>{
                    page: coordinates.pageNumber,
                    x: coordinates.x,
                    y: coordinates.y,
                    name,
                    height: fieldHeightByType(type),
                    value: null,
                    type,
                    assignee,
                    fontSize: 13,
                    order: null,
                    width: fieldWidthByType(type),
                };

                const textAnnot = this.getTextAnnotations(pdfField);

                this.setDdjField(pdfField.name, type, undefined, textAnnot, assignee);

                this.fieldIteration++;
            }
        });
    }

    getTextAnnotations(pdfField: PdfField) {
        if (!pdfField.name || !this.pdfTron?.Core?.Annotations) {
            return;
        }
        const { Annotations } = this.pdfTron.Core; // as { Annotations: typeof Core.Annotations };
        const textAnnot = new Annotations.FreeTextAnnotation();
        textAnnot.PageNumber = pdfField.page;
        textAnnot.Width = pdfField.width || fieldWidthByType(pdfField.type);
        textAnnot.Height = pdfField.height || fieldHeightByType(pdfField.type);
        textAnnot.X = pdfField.x;
        textAnnot.Y = pdfField.y;

        textAnnot.setPadding(new Annotations.Rect(0, 0, 0, 0));
        const name = pdfField.name || `${pdfField.assignee}_${pdfField.type}_${this.fieldIteration}`;
        textAnnot.custom = {
            assignee: pdfField.assignee,
            flag: null,
            name,
            type: pdfField.type,
            value: pdfField.value,
            font: {
                //size: '12',
                name: 'Courier New Bold',
                strokeColor: new Annotations.Color(0, 0, 0, 1),
                fillColor: new Annotations.Color(0, 0, 0, 1),
            },
        } as CustomAnnotation;

        try {
            // Certain PDFTron actions may cause this to be left undefined
            if (pdfField.assignee === undefined) {
                pdfField.assignee = getAssigneeFromName(pdfField.name);
            }
            const { fill, text, stroke } = getColorsByAssignee(pdfField.assignee, Annotations.Color);
            textAnnot.FillColor = fill;
            textAnnot.TextColor = text;
            textAnnot.StrokeColor = stroke;
        } catch (ex) {
            console.error(ex);
        }

        textAnnot.enableRotationControl(false);
        textAnnot.setContents(textAnnot.custom.name);
        textAnnot.FontSize = '13px';
        textAnnot.StrokeThickness = 2;
        textAnnot.TextAlign = 'left';

        const annotManager = this.pdfTron.Core.documentViewer.getAnnotationManager();
        textAnnot.Author = annotManager.getCurrentUser();
        return textAnnot;
    }

    setTextAnnotFromDom(assignee: Assignees, type: FieldTypes, point: { x; y } = { x: 0, y: 0 }, fieldName = '', value = '') {
        const documentViewer = this.pdfTron?.Core?.documentViewer as Core.DocumentViewer;
        if (!documentViewer) {
            return;
        }
        const doc = documentViewer.getDocument();
        const displayMode = documentViewer.getDisplayModeManager().getDisplayMode();
        const page = displayMode.getSelectedPages(point, point);
        if (!!point.x && page.first == null) {
            return null; //don't add field to an invalid page location
        }
        const page_idx = page.first !== null ? page.first : documentViewer.getCurrentPage();
        const page_info = doc.getPageInfo(page_idx);
        const page_point = displayMode.windowToPage(point, page_idx);
        const width = fieldWidthByType(type);

        if (!fieldName) {
            fieldName = this.getFieldName(assignee, type);
        }

        const pdfField = <PdfField>{
            page: page_idx,
            x: (page_point.x || page_info.width / 2) - width / 2,
            y: (page_point.y || page_info.height / 2) - 20.0 / 2,
            name: fieldName,
            height: fieldHeightByType(type),
            value: null,
            type,
            assignee,
            fontSize: 13,
            order: null,
            width,
        };

        const textAnnot = this.getTextAnnotations(pdfField);

        this.setDdjField(pdfField.name, type, value, textAnnot, assignee);
        return textAnnot;
    }

    addField(textAnnot) {
        const { documentViewer } = this.pdfTron.Core;
        const annotManager = documentViewer.getAnnotationManager();
        annotManager.deselectAllAnnotations();
        annotManager.addAnnotation(textAnnot, true);
        annotManager.redrawAnnotation(textAnnot);
    }

    deleteFields(collection: any[]) {
        collection.forEach((toDelete, index) => {
            // kga is built in pdftron property... Custom may not exist if deleting
            // a field that was moved from one page to another.
            const name = toDelete.custom?.name || toDelete.kga;
            if (name && this.fields[name]) {
                delete this.fields[name];
                if (this.fieldIteration > 0) {
                    this.fieldIteration--;
                }
            }
        });
    }

    getFieldName(assignee: Assignees, type: FieldTypes): string {
        let startOfName = `${assignee}_${type}`;

        // These fields don't really have an association to an assignee
        // since they will be added as readonly fields
        // is assignee === deal
        if (assignee === Assignees.deal) {
            startOfName = `${type}`;
        }

        let name = `${startOfName}_${this.fieldIteration + 1}`;

        // Just a fail safe in the case a user removes an older annotation and then
        // tries to add the same annotation that was just added. This will prevent
        // any value overwriting
        let secondaryCounter = 1;
        // Check if defaultName already exists
        while (name.trim() !== '' && Object.keys(this.fields).includes(name)) {
            secondaryCounter++;
            name = `${startOfName}_${this.fieldIteration + 1}_${secondaryCounter}`;
        }

        return name;
    }

    dragEnd(e: DragEvent, type: FieldTypes, assignee: Assignees) {
        const pos = this.dropPoint ?? getHtmlCenter(this.pdfViewerRef.nativeElement, true);
        this.addField(this.setTextAnnotFromDom(assignee, type, pos));
        (<HTMLElement>e.target).style.opacity = '1';
        document.body.removeChild(document.getElementById('form-build-drag-image-copy'));
        e.preventDefault();
    }

    dragOver(e: DragEvent) {
        e.preventDefault();
        return false;
    }

    dragStart(e: DragEvent) {
        (<HTMLElement>e.target).style.opacity = '0.5';
        const copy = <HTMLElement>(<HTMLElement>e.target).cloneNode(true);
        copy.id = 'form-build-drag-image-copy';
        document.body.appendChild(copy);

        const selectedRect = (<HTMLElement>e.target).getBoundingClientRect();

        const x = selectedRect.right;
        const y = selectedRect.top;

        e.dataTransfer.setDragImage(copy, x, y);
        e.dataTransfer.setData('text', '');
    }

    drop(e: DragEvent, instance) {
        const { documentViewer } = instance.Core;
        const scrollElement = documentViewer.getScrollViewElement();
        const scrollLeft = scrollElement.scrollLeft || 0;
        const scrollTop = scrollElement.scrollTop || 0;
        this.dropPoint = { x: e.pageX + scrollLeft, y: e.pageY + scrollTop };
        e.preventDefault();
        return false;
    }

    async annotationFlow$(annotationManager, annot) {
        await annotationManager.jumpToAnnotation(annot, {
            zoom: this.isMobileDevice ? '2.5' : '3.5',
            isSmoothScroll: true,
        });
    }

    async jumpToAnnotation$(annot) {
        const settings: any = {
            isSmoothScroll: true,
            zoom: '2',
        };

        // If a field is too close to the bottom on mobile devices the
        // guided signing button will overlay
        if (this.isMobileDevice && annot.rect.y2 > 700) {
            settings.horizontalOffset = '100%';
            settings.zoom = '2.5';
        }

        await this.pdfTron.Core.annotationManager.jumpToAnnotation(annot, settings);
    }

    moveEvent(collection: any[]) {
        collection.forEach((activeField) => {
            // edge case if someone moves an annotation from one page to
            // another and then tries to move it again
            if (activeField.custom === undefined && activeField.kga && this.fields[activeField.kga] !== undefined) {
                activeField.custom = this.fields[activeField.kga];
            }

            if (activeField && activeField.custom) {
                // check to see if the activeField overlaps another annotation
                resetPriority(this.pdfTron, activeField);

                this.setDdjField(activeField.custom.name, activeField.custom.type, activeField.custom.value, activeField);
            }
        });
    }

    setDdjField(name: string, type: FieldTypes, value, meta, assignee?: Assignees) {
        if (name) {
            const field: PdfField = this.fields[name] || <PdfField>{};

            // If a user is pulling an annotation between pages this could get messed up
            // which is why we do this check
            if (Object.keys(field).length <= 0) {
                field.assignee = getAssigneeFromName(name);
            }

            this.fields[name] = {
                ...field,
                ...getDdjField(name, meta, field.assignee || assignee, type, value),
            };
        }
    }

    async setDocumentBaseline(pdf) {
        this.webviewerInError = false;
        if (pdf.data) {
            //const baselineContent = await pdf.data.content.arrayBuffer();
            //instance.baselineContent = new Blob([baselineContent]);
            //await instance.pdfTron.Core.documentViewer.loadDocument(pdf);

            if (pdf.data.content) {
                const binary_string = window.atob(await pdf.data.content);
                const len = binary_string.length;
                const bytes = new Uint8Array(len);
                for (let i = 0; i < len; i++) {
                    bytes[i] = binary_string.charCodeAt(i);
                }

                const file = new Blob([bytes], { type: pdf.data.contentType });
                this.baselineContent = file;
                await this.pdfTron.Core.documentViewer.loadDocument(file, { extension: 'pdf' });
                this.iFrameLoaded.next(true);
            } else {
                const path = pdf.data.filename;
                this.baselineContent = pdf.data.filename;
                if (this?.pdfTron?.Core?.documentViewer) {
                    await this.pdfTron.UI.loadDocument(path, {
                        extension: 'pdf',
                        onLoadingProgress: (percent) => {
                            console.log(`Loading: ${(percent * 100).toFixed(2)}%`);
                        },
                        onError: (error) => {
                            this.zone.run(() => {
                                console.error('Error in loadDocument:', error);
                                this.webviewerInError = true;
                                this.iFrameLoaded.next(false);
                            });
                        },
                    });
                    this.iFrameLoaded.next(true);
                }
            }
        } else {
            const baselineContent = pdf?.length > 0 ? pdf : new Blob([await pdf.arrayBuffer()]);
            this.baselineContent = baselineContent;
            await this.pdfTron.UI.loadDocument(pdf, {
                loadAsPDF: true,
            });
            this.iFrameLoaded.next(true);
        }
    }

    showAnnotations$(
        assignee?: Assignees,
        dealAnnotationLoaded = false,
        signedMetaData?: [{ [key: string]: PdfField }],
        extension: 'pdf' | 'jpg' | 'png' = 'pdf'
    ) {
        // reset field iteration to maintain a consistent count
        this.fieldIteration = 0;

        this.resetFields();

        // Only load the document if it isn't already being loaded
        if (this.loadingDocument) {
            this.loadingDocument = false;
            return Promise.resolve();
        } else {
            if (!('Core' in this.pdfTron)) {
                return Promise.resolve();
            }

            this.loadingDocument = true;
            return this.pdfTron.Core.documentViewer.loadDocument(this.baselineContent, { loadAsPdf: true, extension }).then(() => {
                this.addFields(this.fields, assignee, dealAnnotationLoaded, signedMetaData);
                // Since there are occurrences where this gets fired multiple times in a row
                // we have the if statement above, but the setTimeout allows us to load things
                // properly when someone uses this function only once
                setTimeout(() => (this.loadingDocument = false), 1000);
            });
        }
    }

    resetFields() {
        // Make sure it is a copy of fields and not pointing to the same reference
        // in both conditions

        if (this.fields !== undefined && Object.keys(this.fields).length > 0) {
            // If we have more than one field present in fields we reset fieldsBkup
            // to what fields currently is
            this.fieldsBkup = JSON.parse(JSON.stringify(this.fields));
        } else if (
            this.fields !== undefined &&
            Object.keys(this.fields).length <= 0 &&
            this.fieldsBkup !== undefined &&
            Object.keys(this.fieldsBkup).length > 0
        ) {
            // This is for scenarios like testing fields where there are no annotations on the
            // document, but we still want to retain the fields, so we pull from the back up
            this.fields = JSON.parse(JSON.stringify(this.fieldsBkup));
        }
    }

    // This scenario is very tricky... This is primarily to fix the fault in resetFields() where it
    // does not know how to handle the scenario where a user manually removed all the annotations
    // on an editing document. If the user performed that action, we clear the back up because they
    // have shown they no longer need those fields.
    public handleAllFieldsRemovedCase() {
        // If this condition is hit, the user removed all annotations from the document themselves, so
        // we no longer care about maintainng the back up for them.
        if (Object.keys(this.fields || {}).length <= 0 && Object.keys(this.fieldsBkup).length > 0) {
            this.fieldsBkup = {};
        }
    }

    async getSignatureImages(allowNoSignature = false) {
        const res = await firstValueFrom(
            forkJoin({
                signature: this.userService.getUserSignature$(1),
                initials: this.userService.getUserSignature$(2),
            })
        );

        let noSignature = false;
        const missingRedirectPage = 'interactionInt';

        if (res.signature?.data && res.signature.data.length > 0) {
            this.signature = res.signature;
        } else {
            noSignature = true;
        }

        if (res.initials?.data && res.initials.data.length > 0) {
            this.initials = res.initials;
        } else {
            noSignature = true;
        }

        if (noSignature && !allowNoSignature) {
            this.userService.routeHasMissingSignaturesError(null, null, missingRedirectPage);
            // reset in case the user comes back in the same session
            return;
        }

        this.signaturesReady.next(true);
    }

    turnGuidedSigningOn() {
        this.isGuidedSigningOn.next(true);
    }

    async advanceGuidedSigning(assignee: Assignees): Promise<GuidedSigningState> {
        this.turnGuidedSigningOn();

        const guide = this.getGuidedSigningState(assignee);

        if (guide?.nextField) {
            await this.jumpToAnnotation$(guide.nextField);
        }

        return guide;
    }

    getGuidedSigningState(assignee: Assignees): GuidedSigningState {
        if (!('Core' in this.pdfTron)) {
            return null;
        }

        const { documentViewer } = this.pdfTron.Core;
        const annotManager = documentViewer.getAnnotationManager();
        const annotations = annotManager.getAnnotationsList();
        let nextName = undefined;
        let nextField = undefined;
        let fieldsToComplete = 0;
        let emptyOptionalChecks = 0;
        let nextFieldSet = false;

        // Sort the fields by page number and Y positioning
        const currentList = Object.values(this.fieldsBkup).sort((a, b) => {
            if (a.page === b.page) {
                return a.y - b.y;
            }

            return a.page > b.page ? 1 : -1;
        });

        for (const current of currentList) {
            const isOptional =
                current?.type === FieldTypes.optionalSignature ||
                current?.type === FieldTypes.optionalInitials ||
                current?.type === FieldTypes.optionalLegalName ||
                current?.type === FieldTypes.optionalText;

            if (current) {
                if (current.assignee !== assignee) {
                    continue;
                }
                // Check if the field has a widget and potentially signature
                const currentWidget = annotations.find((a) => a.Subject === 'Widget' && a.fieldName === `widget_${current.name}`);

                if (currentWidget) {
                    // properly set the values on the fields and remove widget from document
                    switch (current.type) {
                        case FieldTypes.signature:
                        case FieldTypes.initials:
                        case FieldTypes.legalName:
                        case FieldTypes.optionalLegalName:
                        case FieldTypes.optionalInitials:
                        case FieldTypes.optionalSignature:
                            // check if there is a signature attached to the widget
                            // eslint-disable-next-line no-case-declarations
                            const signature = annotations.find((a: any) => a.Subject === 'Signature' && a.Id === currentWidget.annot?.Id);

                            if (!signature) {
                                fieldsToComplete++;

                                if (!nextField) {
                                    nextField = currentWidget;
                                }
                            } else if (isOptional) {
                                emptyOptionalChecks--;
                            }
                            break;
                        default:
                            // Account for default values
                            if (
                                !currentWidget.value ||
                                currentWidget.value === 'mm-dd-yyyy' ||
                                (current.type === FieldTypes.date && isNaN(Date.parse(currentWidget.value)))
                            ) {
                                fieldsToComplete++;

                                if (!nextField) {
                                    nextField = currentWidget;
                                }
                            } else if (isOptional) {
                                emptyOptionalChecks--;
                            }
                            break;
                    }
                }
            }

            // Clear out nextField if optional and already setAsActive
            if (isOptional && current.setAsActive) {
                nextField = undefined;
                emptyOptionalChecks++;
            } else if (nextField && !nextFieldSet) {
                nextName = current.name;
                nextFieldSet = true;
            }
        }

        if (!nextField) {
            this.isDocumentComplete = true;
        }

        if (nextField && this.fieldsBkup[nextName]) {
            this.fieldsBkup[nextName].setAsActive = true;
        }

        return {
            nextField,
            fieldsToComplete,
            emptyOptionalChecks,
        } as GuidedSigningState;
    }

    private handleRouteOneFieldProcessing(field: PdfField, doc: any): boolean {
        // Not adding non-signature fields to a RouteOne document
        if (field?.thirdPartyType === ThirdPartyType.ROUTE_ONE && field.type !== 'SIGNATURE' && field.type !== 'INITIALS' && field.type !== 'LEGAL_NAME') {
            return false;
        } else if (field?.thirdPartyType === ThirdPartyType.ROUTE_ONE) {
            // convert the pdf coordinates into webviewer coordinates
            const newCoordinates = doc.getViewerCoordinates(field.page, field.x, field.y);
            field.x = newCoordinates.x;
            // Must subtract the height to match the original PDF coordinate
            field.y = newCoordinates.y - field.height;
        }

        return true;
    }

    public isInvalidDealerOnly(fields: any) {
        return Object.keys(fields).some((key) => key.includes('buyer_') || key.includes('cobuyer_'));
    }

    public displayInvalidDealerOnlyMessage() {
        this.errorService.showError({
            message:
                'The document is marked as "Dealer Only", but has fields that require customer signatures. Please remove the signatures, or remove the dealer only requirement.',
            title: 'Dealer Only Document',
        });
    }

    public unload() {
        this.iFrameLoaded.next(false);
        this.pdfTron = null;
        this.fields = {};
        this.fieldsBkup = {};
        this.baselineContent = null;
        this.pdfViewerRef = null;
        this.isDocumentComplete = false;
        this.isGuidedSigningOn.next(false);
    }
}
